import React, { useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import axios from 'axios';
import { Play, Calendar, X } from 'lucide-react';
import { Dialog, DialogContent } from '../../components/ui/dialog';

const Releases = () => {
  const { t, API, settings } = useApp();
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);

  // Check if embedded player is enabled (default true)
  const embedEnabled = settings?.player_settings?.releases_embed !== false;

  useEffect(() => {
    const fetchReleases = async () => {
      try {
        const response = await axios.get(`${API}/public/releases`);
        setReleases(response.data.videos || []);
      } catch (error) {
        console.error('Failed to fetch releases:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchReleases();
  }, [API]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleVideoClick = (e, release) => {
    if (embedEnabled) {
      e.preventDefault();
      setSelectedVideo(release);
      // Track analytics
      axios.post(`${API}/public/analytics/track`, { route: `/releases/${release.video_id}` }).catch(() => {});
    }
  };

  return (
    <div className="min-h-screen bg-brand-void">
      {/* Hero */}
      <section className="pt-32 pb-16 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-syne font-bold text-4xl md:text-5xl lg:text-6xl text-white mb-4 reveal" data-testid="releases-title">
            {t('releases.title', 'Releases')}
          </h1>
          <div className="flex items-center gap-4">
            <div className="h-1 w-16 bg-brand-gold rounded-full" />
            <p className="text-white/50 text-lg reveal" style={{ animationDelay: '0.2s' }}>
              All music releases and singles
            </p>
          </div>
        </div>
      </section>

      {/* Releases Grid */}
      <section className="pb-24 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {[...Array(9)].map((_, i) => (
                <div key={i} className="bg-brand-dark rounded-lg overflow-hidden animate-pulse">
                  <div className="aspect-video bg-brand-charcoal" />
                  <div className="p-4">
                    <div className="h-5 bg-brand-charcoal rounded w-3/4 mb-2" />
                    <div className="h-4 bg-brand-charcoal rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : releases.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {releases.map((release, index) => (
                <a
                  key={release.video_id}
                  href={`https://www.youtube.com/watch?v=${release.video_id}`}
                  target={embedEnabled ? undefined : "_blank"}
                  rel={embedEnabled ? undefined : "noopener noreferrer"}
                  onClick={(e) => handleVideoClick(e, release)}
                  className="group bg-brand-dark rounded-lg overflow-hidden reveal card-hover border border-white/5 hover:border-brand-gold/50 cursor-pointer"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`release-card-${release.video_id}`}
                >
                  <div className="relative aspect-video">
                    <img
                      src={release.thumbnail}
                      alt={release.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-brand-red flex items-center justify-center glow-red transform scale-0 group-hover:scale-100 transition-transform duration-300">
                        <Play className="w-7 h-7 text-white fill-white" />
                      </div>
                    </div>
                    {release.featured && (
                      <span className="absolute top-3 left-3 px-3 py-1 bg-brand-gold text-white text-xs font-semibold rounded-full">
                        Featured
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-syne font-semibold text-white text-lg line-clamp-2 group-hover:text-brand-gold transition-colors mb-2">
                      {release.title}
                    </h3>
                    {release.published_at && (
                      <div className="flex items-center gap-2 text-white/40 text-sm">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(release.published_at)}</span>
                      </div>
                    )}
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <div className="text-center py-24">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-brand-dark flex items-center justify-center">
                <Play className="w-10 h-10 text-white/30" />
              </div>
              <h3 className="font-syne text-xl text-white mb-2">No Releases Yet</h3>
              <p className="text-white/50">Music releases will appear here once configured in the admin panel.</p>
            </div>
          )}
        </div>
      </section>

      {/* Video Player Modal */}
      <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
        <DialogContent className="max-w-5xl w-full p-0 bg-black border-white/10 overflow-hidden">
          <button
            onClick={() => setSelectedVideo(null)}
            className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-black/50 backdrop-blur flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            data-testid="close-video-modal"
          >
            <X className="w-5 h-5" />
          </button>
          {selectedVideo && (
            <div className="aspect-video">
              <iframe
                src={`https://www.youtube.com/embed/${selectedVideo.video_id}?autoplay=1&rel=0`}
                title={selectedVideo.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Releases;
